import React, { useState, useEffect } from 'react';
import { Lead } from '../types';
import { consultoresService, leadsService } from '../services/api';

interface BulkAssignModalProps {
  selectedLeads: Lead[];
  onClose: () => void;
  onAssignComplete: () => void;
}

const BulkAssignModal: React.FC<BulkAssignModalProps> = ({ selectedLeads, onClose, onAssignComplete }) => {
  const [consultores, setConsultores] = useState<any[]>([]);
  const [selectedConsultor, setSelectedConsultor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadConsultores();
  }, []);

  const loadConsultores = async () => {
    try {
      const response = await consultoresService.getConsultores();
      setConsultores(response.data || response);
    } catch (error) {
      console.error('Erro ao carregar consultores:', error);
    }
  };

  const handleAssign = async () => {
    if (!selectedConsultor) {
      setError('Selecione um consultor');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Atualizar cada lead selecionada
      await Promise.all(
        selectedLeads.map(lead => 
          leadsService.updateLead(lead.id, { consultor: selectedConsultor })
        )
      );
      
      onAssignComplete();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao atribuir leads');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              Atribuir Leads em Lote
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-4">
              Você está atribuindo <strong>{selectedLeads.length} leads</strong> para um consultor.
            </p>

            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Leads selecionadas:</h4>
              <div className="max-h-32 overflow-y-auto bg-gray-50 rounded-md p-3">
                {selectedLeads.map(lead => (
                  <div key={lead.id} className="text-xs text-gray-600 mb-1">
                    {lead.reu_nome || lead.numero_processo} 
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                👤 Selecionar Consultor
              </label>
              <select
                value={selectedConsultor}
                onChange={(e) => setSelectedConsultor(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="">Escolher consultor...</option>
                {consultores.map(consultor => (
                  <option key={consultor.id} value={consultor.id}>
                    {consultor.name} ({consultor.role})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleAssign}
              disabled={loading || !selectedConsultor}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Atribuindo...' : 'Atribuir Leads'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkAssignModal;